import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Navigation } from "@/components/Navigation";
import { UserTable } from "@/components/admin/UserTable";
import { AddUserDialog } from "@/components/admin/AddUserDialog";
import { RecentActivityWidget } from "@/components/admin/RecentActivityWidget";
import { Button } from "@/components/ui/button";
import { UserPlus } from "lucide-react";
import { toast } from "sonner";

interface UserProfile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  phone_verified: boolean | null;
  avatar_url: string | null;
  created_at: string;
}

interface UserWithProfile {
  id: string;
  email: string;
  created_at: string;
  profile: UserProfile | null;
  roles: string[];
}

export default function AdminUsers() {
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState<UserWithProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  const fetchUsers = async () => {
    try {
      setLoading(true);

      // Get current session for authentication
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      // Call the get-users edge function to fetch all user data including emails
      const { data, error } = await supabase.functions.invoke('get-users', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setUsers(data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    }
  }, [isAdmin]);

  const handleUserAdded = () => {
    fetchUsers();
    setIsAddDialogOpen(false);
  };

  if (!isAdmin) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen flex items-center justify-center">
          <p className="text-muted-foreground">Access denied. Admin privileges required.</p>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-background pt-24 pb-12">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold text-foreground mb-2">User Management</h1>
              <p className="text-muted-foreground">Manage technicians and admin accounts</p>
            </div>
            <Button onClick={() => setIsAddDialogOpen(true)}>
              <UserPlus className="mr-2 h-4 w-4" />
              Add User
            </Button>
          </div>

          <UserTable 
            users={users} 
            loading={loading} 
            onRefresh={fetchUsers}
          />

          <div className="grid gap-6 mt-6">
            <RecentActivityWidget />
          </div>

          <AddUserDialog 
            open={isAddDialogOpen}
            onOpenChange={setIsAddDialogOpen}
            onUserAdded={handleUserAdded}
          />
        </div>
      </div>
    </>
  );
}
